/* ============================================================
   nooa-shopify-bridge.js — Dawn/theme-editor integration ONLY.

   The copied reference scripts (nooa-main.js, nooa-header.js, nooa-marquee.js,
   nooa-slider.js, nooa-accordion.js, nooa-tabs.js, …) are pristine carries. Every
   one of them is an IIFE that queries the DOM once at load and binds what it finds.
   That is correct for a static page, and wrong in exactly one place: the Shopify
   theme editor tears a section out and re-inserts fresh markup at runtime, so any
   node created after load has no observer and no listeners on it.

   This file is the seam. It listens for Shopify's editor events and re-runs the
   per-section init, so the copied files never have to learn about Shopify.

   Sections register themselves here via:
     window.nooa.register('<section-type>', function (root) { … });
   `root` is the section container element. Handlers must be IDEMPOTENT — the same
   root can be handed back after a settings change — so guard with a data flag.
   ============================================================ */
(function () {
  'use strict';

  var registry = Object.create(null);

  var nooa = (window.nooa = window.nooa || {});

  nooa.register = function (type, init) {
    (registry[type] = registry[type] || []).push(init);
  };

  function run(type, root) {
    var inits = registry[type];
    if (!inits || !root) return;
    inits.forEach(function (init) {
      try {
        init(root);
      } catch (err) {
        // One broken section must never take the rest of the page down.
        console.error('[nooa] init failed for section "' + type + '"', err);
      }
    });
  }

  /* Scroll-reveal: [data-animate] starts at opacity 0 and is unhidden by an
     IntersectionObserver that nooa-main.js wired at load. A section re-rendered by
     the editor would keep that starting state forever and read as a blank slab, so
     reveal its nodes immediately — in the editor the merchant is looking straight
     at the section they just edited. */
  function reveal(root) {
    root.querySelectorAll('[data-animate]').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  function sectionType(root) {
    return (root && root.dataset && root.dataset.nooaSection) || '';
  }

  /* Re-run a pristine reference script that exposes no public entrypoint.
     Several of the copied files are bare IIFEs: they query the DOM once at load and
     bind to what they find. That is correct on the storefront, where the markup never
     changes under them, but in the theme editor the section is replaced wholesale and
     the new nodes have nothing bound. Re-inserting the <script> re-runs the IIFE
     against the fresh DOM.

     Deliberately gated to design mode: each re-run adds one more document-level
     listener, which is a slow leak we accept while a merchant edits and refuse to
     ship to shoppers. Scripts that DO expose an entrypoint (nooa-marquee.js ->
     window.__marqueeInitTicker) must use that instead of this. */
  var reinjected = Object.create(null);
  nooa.reinject = function (src) {
    if (!window.Shopify || !window.Shopify.designMode) return;
    var bust = (reinjected[src] = (reinjected[src] || 0) + 1);
    var old = document.querySelector('script[data-nooa-reinject="' + src + '"]');
    if (old) old.parentNode.removeChild(old);
    var el = document.createElement('script');
    el.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'nooa_reinit=' + bust;
    el.setAttribute('data-nooa-reinject', src);
    document.head.appendChild(el);
  };

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-nooa-section]') || event.target;
    reveal(root);
    run(sectionType(root), root);
  });

  /* Block-level events fire without a section reload, so anything that measures or
     re-binds per block needs the same treatment. */
  ['shopify:block:select', 'shopify:block:deselect'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      var root = event.target.closest('[data-nooa-section]');
      if (root) run(sectionType(root), root);
    });
  });
})();
